import type { UseQueryOptions } from "@pinia/colada";
import type { ObjectToCamel } from "ts-case-convert";

import { createError } from "#app/composables/error";
import { objectToCamel } from "ts-case-convert";
import { toValue } from "vue";

import type { StatamicCollection } from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";

import { statamicCollectionEntries } from "@solvari/statamic";
import { toSearchParams } from "@solvari/utils";

import type { StatamicPreview } from "~/shared/composables/useStatamicPreview.ts";

import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { toInternalUrl } from "~/shared/composables/useHost.ts";
import { useStatamicPreview } from "~/shared/composables/useStatamicPreview.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

// Through the proxy: /cache-proxy/cms gives each CMS response its own cache entry.
function cmsCollectionEntriesUrl(collection: StatamicCollection): string {
  return `${toInternalUrl("/cache-proxy/cms")}${statamicCollectionEntries(collection)}`;
}

function useCmsEntryQuery<
  Response extends { data: unknown[] },
  Entry = ObjectToCamel<Response>["data"][number],
>(
  options: {
    blueprint: string;
    collection: StatamicCollection;
    locale: LocaleMorpheus;
    uri: string;
  },
  { previewToken, previewSite }: StatamicPreview = useStatamicPreview(),
) {
  const cmsUrl = cmsCollectionEntriesUrl(options.collection);

  return {
    key: () => [
      "cms",
      options.blueprint,
      { locale: options.locale, uri: options.uri },
      toValue(previewToken),
    ],
    query: handleNuxtQuery(async () => {
      const result = objectToCamel(
        await kyCacheTags
          .get(cmsUrl, {
            searchParams: toSearchParams(
              {
                filter: {
                  blueprint: options.blueprint,
                  site: previewSite.value ?? options.locale,
                  uri: options.uri,
                  status: previewToken.value ? "any" : undefined,
                },
                limit: 1,
                token: previewToken.value,
              },
              { nestedKeys: true },
            ),
          })
          .json<Response>(),
      );

      const page = result.data[0] as Entry | undefined;

      if (!page) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      return page;
    }),
    staleTime: 5 * 60 * 1000,
  } satisfies UseQueryOptions;
}

export { cmsCollectionEntriesUrl, useCmsEntryQuery };
